import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Ban,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  ShieldOff,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@sportscanner/ui";
import { apiGet, apiPost, getApiErrorCode } from "@/lib/api";
import ClubScopeShell from "@/components/club-scope-shell";
import ConfirmDialog from "@/components/confirm-dialog";

type Status = "PENDING" | "APPROVED" | "REJECTED";
type BanDuration = "7d" | "30d" | "90d" | "never";

interface MembershipRow {
  id: string;
  status: Status;
  requestedAt: string;
  decidedAt: string | null;
  player: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
}

interface MembershipsResponse {
  memberships: MembershipRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  counts: Record<Status, number>;
}

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export default function PlayersPage() {
  const { t } = useTranslation();
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
          {t("players.title")}
        </h1>
        <p className="text-sm text-neutral-500">{t("players.subtitle")}</p>
      </div>

      <ClubScopeShell>{(clubId) => <Inner clubId={clubId} />}</ClubScopeShell>
    </div>
  );
}

function Inner({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<Status>("APPROVED");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const debouncedQuery = useDebouncedValue(query, 300);

  // Switching tab or refining the search invalidates the page cursor — paged
  // results from a previous filter would be incoherent otherwise.
  useEffect(() => {
    setPage(1);
  }, [status, debouncedQuery, clubId]);

  const queryString = (() => {
    const params = new URLSearchParams();
    params.set("status", status);
    if (debouncedQuery.trim()) params.set("query", debouncedQuery.trim());
    params.set("page", String(page));
    params.set("pageSize", String(PAGE_SIZE));
    return params.toString();
  })();

  const listQuery = useQuery({
    queryKey: ["admin", "players", clubId, queryString],
    queryFn: () =>
      apiGet<MembershipsResponse>(
        `/api/admin/clubs/${clubId}/players?${queryString}`,
      ),
    placeholderData: keepPreviousData,
  });

  async function invalidate() {
    await queryClient.invalidateQueries({
      queryKey: ["admin", "players", clubId],
    });
  }

  const data = listQuery.data;
  const rows = data?.memberships ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;
  const counts = data?.counts ?? { PENDING: 0, APPROVED: 0, REJECTED: 0 };

  return (
    <div className="space-y-4">
      <Tabs value={status} onValueChange={(v) => setStatus(v as Status)}>
        <TabsList>
          <TabsTrigger value="APPROVED" className="gap-2">
            {t("players.tabs.APPROVED")}
            <span className="text-xs text-neutral-400">{counts.APPROVED}</span>
          </TabsTrigger>
          <TabsTrigger value="PENDING" className="gap-2">
            {t("players.tabs.PENDING")}
            {counts.PENDING > 0 && (
              <Badge className="ml-1 bg-amber-100 text-amber-700 hover:bg-amber-100">
                {counts.PENDING}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="REJECTED" className="gap-2">
            {t("players.tabs.REJECTED")}
            <span className="text-xs text-neutral-400">{counts.REJECTED}</span>
          </TabsTrigger>
        </TabsList>

        <div className="mt-4 relative w-full sm:w-80">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("players.filters.searchPlaceholder")}
            className="pl-9 bg-white border-neutral-200 w-full"
          />
        </div>

        <TabsContent value={status} className="mt-4">
          {listQuery.isLoading ? (
            <div className="bg-white rounded-xl border border-neutral-200 p-8 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
            </div>
          ) : listQuery.isError ? (
            <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
              <p className="text-sm text-neutral-500">
                {t("errors.api.INTERNAL_ERROR")}
              </p>
            </div>
          ) : (
            <PlayersTable
              rows={rows}
              status={status}
              isFetching={listQuery.isFetching}
              hasQuery={!!debouncedQuery.trim()}
              onChanged={invalidate}
            />
          )}

          {total > 0 && (
            <div className="mt-3 flex items-center justify-between text-sm text-neutral-500">
              <span>
                {t("players.pagination.totalCount", { count: total })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t("players.pagination.previous")}
                </Button>
                <span>
                  {t("players.pagination.page", { page, total: totalPages })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("players.pagination.next")}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PlayersTable({
  rows,
  status,
  isFetching,
  hasQuery,
  onChanged,
}: {
  rows: MembershipRow[];
  status: Status;
  isFetching: boolean;
  hasQuery: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();

  // Confirm dialog state for revocation. Reject is also confirmed since it
  // sends an email to the player — irreversible from their perspective.
  const [pendingAction, setPendingAction] = useState<{
    type: "approve" | "reject" | "revoke";
    row: MembershipRow;
  } | null>(null);
  // Ban needs its own state + a duration picker, separate from the simple
  // confirm-dialog mutations above.
  const [banTarget, setBanTarget] = useState<MembershipRow | null>(null);

  const mutation = useMutation({
    mutationFn: async ({
      type,
      row,
    }: {
      type: "approve" | "reject" | "revoke";
      row: MembershipRow;
    }) => apiPost(`/api/admin/memberships/${row.id}/${type}`, {}),
    onSuccess: async (_data, vars) => {
      toast.success(t(`players.toast.${vars.type}`));
      setPendingAction(null);
      await onChanged();
    },
    onError: (err) => {
      const code = getApiErrorCode(err);
      toast.error(t(`errors.api.${code}`, t("errors.api.INTERNAL_ERROR")));
    },
  });

  const banMutation = useMutation({
    mutationFn: async ({
      row,
      duration,
    }: {
      row: MembershipRow;
      duration: BanDuration;
    }) => apiPost(`/api/admin/memberships/${row.id}/ban`, { duration }),
    onSuccess: async () => {
      toast.success(t("players.toast.ban"));
      setBanTarget(null);
      await onChanged();
    },
    onError: (err) => {
      const code = getApiErrorCode(err);
      toast.error(t(`errors.api.${code}`, t("errors.api.INTERNAL_ERROR")));
    },
  });

  function fmtDate(iso: string | null) {
    if (!iso) return "—";
    return new Intl.DateTimeFormat(i18n.language, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(iso));
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 px-6 py-12 text-center">
        <p className="text-sm text-neutral-500">
          {hasQuery ? t("players.noResults") : t(`players.empty.${status}`)}
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className={`bg-white rounded-xl border border-neutral-200 overflow-x-auto ${
          isFetching ? "opacity-80 transition-opacity" : ""
        }`}
      >
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <Th>{t("players.table.name")}</Th>
              <Th>{t("players.table.email")}</Th>
              <Th>{t("players.table.phone")}</Th>
              <Th>
                {status === "PENDING"
                  ? t("players.table.requestedAt")
                  : t("players.table.decidedAt")}
              </Th>
              <th className="px-4 py-2.5 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                {t("players.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-neutral-50">
                <td className="px-4 py-3 text-sm text-neutral-800">
                  {r.player.name || "—"}
                </td>
                <td className="px-4 py-3 text-sm text-neutral-600">
                  {r.player.email}
                </td>
                <td className="px-4 py-3 text-sm text-neutral-600">
                  {r.player.phone || "—"}
                </td>
                <td className="px-4 py-3 text-sm text-neutral-600">
                  {fmtDate(status === "PENDING" ? r.requestedAt : r.decidedAt)}
                </td>
                <td className="px-4 py-3 text-right">
                  {status === "PENDING" && (
                    <div className="inline-flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="bg-primary-600 hover:bg-primary-700 text-white"
                        onClick={() =>
                          setPendingAction({ type: "approve", row: r })
                        }
                      >
                        <Check className="w-4 h-4" />
                        {t("players.actions.approve")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setPendingAction({ type: "reject", row: r })
                        }
                      >
                        <X className="w-4 h-4" />
                        {t("players.actions.reject")}
                      </Button>
                    </div>
                  )}
                  {status === "APPROVED" && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      onClick={() =>
                        setPendingAction({ type: "revoke", row: r })
                      }
                    >
                      <ShieldOff className="w-4 h-4" />
                      {t("players.actions.revoke")}
                    </Button>
                  )}
                  {status === "REJECTED" && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      onClick={() => setBanTarget(r)}
                    >
                      <Ban className="w-4 h-4" />
                      {t("players.actions.ban")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!pendingAction}
        onOpenChange={(o) => {
          if (!o) setPendingAction(null);
        }}
        title={
          pendingAction ? t(`players.confirm.${pendingAction.type}.title`) : ""
        }
        description={
          pendingAction
            ? t(`players.confirm.${pendingAction.type}.description`, {
                name:
                  pendingAction.row.player.name ||
                  pendingAction.row.player.email,
              })
            : undefined
        }
        confirmLabel={
          pendingAction ? t(`players.actions.${pendingAction.type}`) : undefined
        }
        variant={
          pendingAction?.type === "reject" || pendingAction?.type === "revoke"
            ? "destructive"
            : "default"
        }
        isPending={mutation.isPending}
        onConfirm={() => {
          if (pendingAction) mutation.mutate(pendingAction);
        }}
      />

      <BanDialog
        target={banTarget}
        onOpenChange={(o) => {
          if (!o) setBanTarget(null);
        }}
        isPending={banMutation.isPending}
        onConfirm={(duration) => {
          if (banTarget) banMutation.mutate({ row: banTarget, duration });
        }}
      />
    </>
  );
}

function BanDialog({
  target,
  onOpenChange,
  isPending,
  onConfirm,
}: {
  target: MembershipRow | null;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onConfirm: (duration: BanDuration) => void;
}) {
  const { t } = useTranslation();
  const [duration, setDuration] = useState<BanDuration>("30d");

  // Reset to the default whenever the dialog is re-opened so prior selection
  // doesn't leak across players.
  useEffect(() => {
    if (target) setDuration("30d");
  }, [target]);

  const open = !!target;
  const name = target ? target.player.name || target.player.email : "";

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (isPending) return;
        onOpenChange(o);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("players.confirm.ban.title", { name })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("players.confirm.ban.description", { name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 pt-2">
          <p className="text-xs font-medium uppercase tracking-wider text-neutral-500">
            {t("players.confirm.ban.durationLabel")}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {(["7d", "30d", "90d", "never"] as const).map((d) => {
              const selected = duration === d;
              return (
                <button
                  key={d}
                  type="button"
                  disabled={isPending}
                  onClick={() => setDuration(d)}
                  className={`px-3 py-2 rounded-md border text-sm cursor-pointer transition-colors disabled:opacity-50 ${
                    selected
                      ? "border-rose-500 bg-rose-50 text-rose-700 font-semibold"
                      : "border-neutral-200 bg-white hover:border-neutral-300"
                  }`}
                >
                  {t(`players.confirm.ban.durations.${d}`)}
                </button>
              );
            })}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t("common.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(e) => {
              e.preventDefault();
              onConfirm(duration);
            }}
            className="bg-red-600 hover:bg-red-700"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t("players.actions.ban")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
      {children}
    </th>
  );
}
